import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Button } from '@structural-climbing/ui'
import { useMyProfile } from '../profile/useMyProfile'
import { ideasOpen, dismissTip, hasVisitedIdeas, tipDismissed, trophyVisible } from './campaign'

export function TrophyIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M8 21h8M12 16.5V21M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M17 5.5h3V7a3 3 0 0 1-3 3M7 5.5H4V7a3 3 0 0 0 3 3" />
    </svg>
  )
}

/**
 * The way into "Top 5 din noiembrie", in the gym bar next to the map.
 *
 * Its dot shows until the first visit, then comes back whenever one of your
 * ideas changes status. Both come from what Home already has (this device's
 * memory and the cached profile), so the trophy costs no request.
 *
 * A one-time tip points at it while the campaign is open. Tapping anywhere
 * puts it away for good.
 */
export function IdeasTrophy({ gymSlug }: { gymSlug: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const reduced = useReducedMotion()
  const { profile } = useMyProfile()
  const visible = trophyVisible()
  const campaignOpen = ideasOpen()
  const visited = hasVisitedIdeas()
  const [tipOpen, setTipOpen] = useState(
    () => visible && campaignOpen && !visited && !tipDismissed(),
  )

  const tipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!tipOpen) return
    // A tap anywhere but the tip itself puts it away. Added on the next tick so
    // the tap that brought Home up does not count.
    const close = (e: PointerEvent) => {
      if (tipRef.current?.contains(e.target as Node)) return
      dismissTip()
      setTipOpen(false)
    }
    const id = setTimeout(() => window.addEventListener('pointerdown', close), 0)
    return () => {
      clearTimeout(id)
      window.removeEventListener('pointerdown', close)
    }
  }, [tipOpen])

  if (!visible) return null

  const dot = !visited || profile?.ideasUpdatePending === true
  const open = () => {
    dismissTip()
    void navigate(`/${gymSlug}/ideas`)
  }

  return (
    <div className="relative shrink-0">
      <Button
        isIconOnly
        variant="light"
        radius="full"
        aria-label={dot ? `${t('ideas.title')}. ${t('ideas.tipTag')}` : t('ideas.title')}
        onPress={open}
        className="text-foreground/75"
      >
        <motion.span
          className="flex"
          animate={dot && !reduced ? { rotate: [0, -12, 10, -6, 3, 0] } : { rotate: 0 }}
          transition={{ duration: 0.7, repeat: dot && !reduced ? Infinity : 0, repeatDelay: 2.6 }}
        >
          <TrophyIcon />
        </motion.span>
        {dot && (
          <span
            data-testid="ideas-dot"
            aria-hidden
            className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-[var(--gym-primary)] ring-2 ring-[var(--gym-bar)]"
          />
        )}
      </Button>

      <AnimatePresence>
        {tipOpen && (
          <motion.div
            ref={tipRef}
            role="note"
            initial={{ opacity: 0, scale: 0.7, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26, delay: reduced ? 0 : 0.5 }}
            style={{ transformOrigin: 'calc(100% - 20px) 0' }}
            className="absolute right-0 top-full z-10 mt-2 w-56 rounded-lg bg-foreground p-3 text-[13px] leading-snug text-background shadow-e3"
          >
            <span
              aria-hidden
              className="absolute -top-1.5 right-4 h-3 w-3 rotate-45 rounded-[2px] bg-foreground"
            />
            <span className="block text-[11px] font-bold uppercase tracking-wider text-[var(--gym-primary)]">
              {t('ideas.tipTag')}
            </span>
            {t('ideas.tipText')}
            <Button
              size="sm"
              variant="light"
              onPress={open}
              className="mt-1 h-7 min-w-0 px-0 font-bold text-[var(--gym-primary)] data-[hover=true]:bg-transparent"
            >
              {t('ideas.tipAction')} →
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
